import HOC from "../../components/HOC";
import { useEffect, useState } from "react";
import { InputAdornment, TextField } from "@mui/material";
import OlinMascot from "../../assets/olinCharaLogin.png";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { setResetPassword } from "../../services/authApi";
import ModalPopup from "../../components/common/ModalPopup";
import { BsCheckCircle } from "react-icons/bs";
import FullsizeButton from "../../components/common/FullsizeButton";
import { IoArrowBack } from "react-icons/io5";
import { useFormik } from "formik";
import * as yup from "yup";
import { RxEyeClosed } from "react-icons/rx";
import { VscEye } from "react-icons/vsc";
import AuthButton from "../../components/common/AuthButton";
import { IoInformationCircleOutline } from "react-icons/io5";
import Logo from "../../assets/olinfull.png";

const textFieldStyles = {
  backgroundColor: "white",
  borderRadius: "10px",
  color: "#111827",
  transition: "all 0.4s",
  fontFamily: '"Montserrat", sans-serif',
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "2px",
    borderColor: "#d1d5db",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#9ca3af",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#111827",
    borderWidth: "2px",
  },
  "& .MuiInputBase-input": {
    padding: "14px 12px",
    color: "#111827",
    fontSize: { xs: "14px", lg: "16px" },
    fontFamily: '"Montserrat", sans-serif',
    "&::placeholder": {
      color: "#CBCBCB",
      opacity: 1,
      fontFamily: '"Montserrat", sans-serif',
    },
  },
  "&.Mui-disabled": {
    backgroundColor: "#f9fafb",
    opacity: 0.7,
  },
};

const errorTextFieldStyles = {
  ...textFieldStyles,
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "2px",
    borderColor: "#ef4444",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ef4444",
  },
};

const formHelperFieldStyles = {
  width: "100%",
  display: "flex",
  justifyContent: "start",
  margin: "2px 0 0 0",
  fontSize: "12px",
  color: "#ef4444",
  fontFamily: '"Montserrat", sans-serif',
};

const validationSchema = yup.object({
  token: yup.string().required("Token wajib ada"),
  new_password: yup
    .string()
    .min(8, "Password minimal 8 karakter")
    .required("Password wajib diisi"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("new_password"), null], "Password tidak sama")
    .required("Konfirmasi password wajib diisi"),
});

const NewPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const token = params.get("token");

  const [errorMessage, setErrorMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const getFieldStyles = (hasError) => {
    return hasError ? errorTextFieldStyles : textFieldStyles;
  };

  const formik = useFormik({
    initialValues: {
      token: token,
      new_password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: (value) => {
      const submitData = {
        token: value.token,
        new_password: value.new_password,
      };
      mutateNewPassword(submitData);
    },
    validateOnMount: true,
  });

  const { mutate: mutateNewPassword, isPending } = useMutation({
    mutationFn: setResetPassword,
    onSuccess: () => setOpen(true),
    onError: (err) => setErrorMessage(err.message.id),
  });

  return (
    <div className="bg-white relative">
      <FullsizeButton />
      <div className="flex flex-row justify-center h-screen">
        <div className="hidden md:order-last bg-cover lg:block lg:w-1/2">
          <div className="flex items-center h-full p-8 bg-white justify-center ">
            <div className="w-[30vw] h-[30vw] flex justify-center object-cover items-center rounded-full p-8 bg-blue-100">
              <div className="p-16 bg-blue-300 rounded-full w-full h-full">
                <img
                  src={OlinMascot}
                  className="w-full h-full"
                  alt="Olin Mascot"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-1/2 flex justify-center lg:items-center px-0 lg:px-12 text-base">
          <div className="rounded-md p-4 lg:p-8 w-full h-fit">
            <div className="w-full flex items-center gap-4 lg:hidden text-xl">
              <div
                onClick={() => navigate(-1)}
                className="p-1 hover:bg-gray-200 rounded-full"
              >
                <IoArrowBack className="text-gray-500" />
              </div>
            </div>

            <img
              src={Logo}
              alt="Logo OLIN"
              className={`my-8 ${
                isMobileView ? "w-[80px] mt-12" : "w-[100px] mt-16"
              }`}
            />
            <p
              className={`text-black ${
                isMobileView
                  ? "font-semibold text-xl mt-8"
                  : "font-bold text-3xl my-4"
              }`}
            >
              Reset Password
            </p>
            <p
              className={`my-6 text-disableText ${
                isMobileView ? "text-base" : "text-lg"
              }`}
            >
              Masukkan kata sandi baru yang akan digunakan untuk masuk ke
              akunmu.
            </p>

            <form onSubmit={formik.handleSubmit}>
              <div className="space-y-4">
                <div>
                  <p>Password</p>
                  <TextField
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    name="new_password"
                    placeholder="Masukkan password (min. 8 karakter)"
                    value={formik.values.new_password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.new_password &&
                      Boolean(formik.errors.new_password)
                    }
                    helperText={
                      formik.touched.new_password && formik.errors.new_password
                        ? formik.errors.new_password
                        : ""
                    }
                    disabled={isPending}
                    InputProps={{
                      sx: getFieldStyles(
                        formik.errors.new_password &&
                          formik.touched.new_password
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {showPassword ? (
                            <RxEyeClosed
                              onClick={() => setShowPassword(!showPassword)}
                              className="cursor-pointer"
                            />
                          ) : (
                            <VscEye
                              onClick={() => setShowPassword(!showPassword)}
                              className="cursor-pointer"
                            />
                          )}
                        </InputAdornment>
                      ),
                    }}
                    FormHelperTextProps={{
                      sx: formHelperFieldStyles,
                    }}
                  />
                </div>

                <div>
                  <p>Konfirmasi Password</p>
                  <TextField
                    fullWidth
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Masukkan ulang password"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.confirmPassword &&
                      Boolean(formik.errors.confirmPassword)
                    }
                    helperText={
                      formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                        ? formik.errors.confirmPassword
                        : ""
                    }
                    disabled={isPending}
                    InputProps={{
                      sx: getFieldStyles(
                        formik.errors.confirmPassword &&
                          formik.touched.confirmPassword
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {showConfirmPassword ? (
                            <RxEyeClosed
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              className="cursor-pointer"
                            />
                          ) : (
                            <VscEye
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              className="cursor-pointer"
                            />
                          )}
                        </InputAdornment>
                      ),
                    }}
                    FormHelperTextProps={{
                      sx: formHelperFieldStyles,
                    }}
                  />
                </div>

                {errorMessage && (
                  <div className="w-full flex justify-end mt-2">
                    <div className="flex items-center gap-2 bg-red-100 py-1.5 px-2.5 rounded-md">
                      <IoInformationCircleOutline
                        className="text-error shrink-0"
                        size={12}
                      />
                      <p className="text-sm text-red-700">{errorMessage}</p>
                    </div>
                  </div>
                )}

                <AuthButton
                  title={"Simpan Reset"}
                  isPending={isPending}
                  formik={formik}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <ModalPopup
        open={open}
        setOpen={setOpen}
        path="/login"
        title="Kata Sandi Berhasil Diperbarui"
        subtitle="Silahkan masuk menggunakan kata sandi yang baru."
        icon={
          <>
            <BsCheckCircle className="text-5xl text-green-500" />
          </>
        }
        type={"window-location"}
      />
    </div>
  );
};

export default HOC(NewPassword, "guest-only");
